import { useMemo } from "react";
import type { Insight } from "@/components/schools/AiInsights";
import { useSchoolFilters, termLabel } from "@/components/schools/SchoolFilters";
import { counsellorQueue } from "@/data/greenwoodPersonas";

/**
 * Derives contextual AI insights from the active filter state.
 * Base insights (school-wide) are always kept; filter-scoped insights
 * are prepended so the top card reflects what the presenter is showing.
 */
export function useDynamicInsights(
  base: Insight[],
  scope: "principal" | "counsellor" | "studios" | "portfolio",
): {
  insights: Insight[];
  contextLabel: string | null;
  isFiltered: boolean;
} {
  const [f] = useSchoolFilters();

  return useMemo(() => {
    const filtered = f.grade !== "all" || f.house !== "all" || f.term !== "current";
    const parts: string[] = [];
    if (f.grade !== "all") parts.push(`Grade ${f.grade}`);
    if (f.house !== "all") parts.push(`${f.house} house`);
    if (f.term !== "current") parts.push(termLabel(f.term));
    const contextLabel = filtered ? parts.join(" · ") : null;

    if (!filtered) return { insights: base, contextLabel: null, isFiltered: false };

    const extra: Insight[] = [];

    if (scope === "principal") {
      if (f.grade !== "all") {
        const g = Number(f.grade);
        if (g <= 9) {
          extra.push({
            tone: "opportunity",
            title: `Grade ${g}: identity & exploration window is open`,
            body: `Learners in Grade ${g} are in a high-plasticity window for interests and self-concept. Front-loading Career Clarity 360 modules here compounds through Grades 10–12.`,
            actions: [
              { label: "See Learning Studios", to: `/schools/studios?grade=${g}` },
              { label: "Open a student profile", to: "/schools/student" },
            ],
          });
        } else if (g === 10) {
          extra.push({
            tone: "watch",
            title: "Grade 10: pre-board pressure vs. clarity",
            body: "Academic load ramps sharply in Grade 10. Pair Career Clarity modules with parent conversation starters to protect Human Development scores.",
            actions: [
              { label: "Open Counsellor queue", to: "/schools/counsellor?grade=10" },
              { label: "Send parent guide", to: "/parent-guide" },
            ],
          });
        } else {
          extra.push({
            tone: "positive",
            title: `Grade ${g}: readiness signals are strongest here`,
            body: `Grade ${g} cohort shows the sharpest Future Readiness gains. Anchor a portfolio review to convert clarity into board-pack evidence.`,
            actions: [
              { label: "Open Portfolio", to: `/schools/portfolio?grade=${g}` },
              { label: "Open Board pack", to: "/schools/boardpack" },
            ],
          });
        }
      }
      if (f.house !== "all") {
        extra.push({
          tone: "opportunity",
          title: `${f.house} house: build a signature narrative`,
          body: `Segmenting by ${f.house} lets you spot house-level trends (attendance, reflection, studio adoption) that the school-wide view averages out. Consider a house-vs-house dashboard for the next town hall.`,
          actions: [
            { label: `Counsellor · ${f.house}`, to: `/schools/counsellor?house=${f.house}` },
          ],
        });
      }
      if (f.term !== "current") {
        extra.push({
          tone: "watch",
          title: `${termLabel(f.term)}: comparing against a prior window`,
          body: `You're viewing data scoped to ${termLabel(f.term)}. Deltas against the current term are the most useful signal — call these out explicitly in the board pack.`,
          actions: [
            { label: "Open Board pack", to: "/schools/boardpack" },
          ],
        });
      }
    }

    if (scope === "counsellor") {
      const scoped = counsellorQueue.filter((r) => {
        if (f.grade !== "all" && !r.grade.startsWith(f.grade)) return false;
        if (f.house !== "all" && r.house !== f.house) return false;
        return true;
      });
      const high = scoped.filter((r) => r.priority === "high").length;
      if (scoped.length === 0) {
        extra.push({
          tone: "positive",
          title: "No students match these filters",
          body: "Either this cohort is stable this week, or the filter is too narrow. Reset to see the school-wide picture.",
          actions: [
            { label: "Reset filters", to: "/schools/counsellor" },
          ],
        });
      } else {
        const first = scoped[0];
        extra.push({
          tone: high > 0 ? "watch" : "opportunity",
          title: `${scoped.length} student${scoped.length === 1 ? "" : "s"} in scope · ${high} high priority`,
          body: high > 0
            ? `Prioritise the ${high} high-priority signal${high === 1 ? "" : "s"} first — clarity dips and broken reflection streaks respond fastest to a 15-minute check-in this week.`
            : `Only medium/low signals in this scope. A group session on career narrative could compound the momentum before the next term.`,
          actions: [
            { label: `Open ${first.name}`, to: `/schools/student?student=${first.id}` },
          ],
        });
      }
      if (f.house !== "all") {
        extra.push({
          tone: "opportunity",
          title: `${f.house} house: consider a peer-support pod`,
          body: `Filtering by ${f.house} makes it easy to spot clustering effects. Two students in the same house with adjacent signals often benefit more from a peer pod than 1:1 sessions.`,
          actions: [
            { label: `Principal view · ${f.house}`, to: `/schools?house=${f.house}` },
          ],
        });
      }
    }

    if (scope === "studios") {
      if (f.grade !== "all") {
        const g = Number(f.grade);
        const focus = g <= 9 ? "WALS™ (life skills)" : g === 10 ? "WAFLE™ (financial literacy)" : "WAFFS™ (future skills)";
        extra.push({
          tone: "opportunity",
          title: `Grade ${g}: lead with ${focus}`,
          body: `The strongest developmental fit for Grade ${g} is ${focus}. Sequence assemblies and parent notes around this studio for the next 4 weeks.`,
          actions: [
            { label: "Open Portfolio", to: `/schools/portfolio?grade=${g}` },
            { label: "Counsellor queue", to: `/schools/counsellor?grade=${g}` },
          ],
        });
      }
      if (f.house !== "all") {
        extra.push({
          tone: "opportunity",
          title: `${f.house}: studio adoption varies by house`,
          body: `Houses with higher WALS™ completion tend to lift Human Development scores 4–6 weeks later. Track ${f.house} completion week-over-week and celebrate publicly.`,
          actions: [
            { label: `Principal · ${f.house}`, to: `/schools?house=${f.house}` },
          ],
        });
      }
      if (f.term !== "current") {
        extra.push({
          tone: "watch",
          title: `${termLabel(f.term)}: retro on module cliffs`,
          body: `Prior-term data is best used to spot completion cliffs. Compare WAFLE™ Credit module completion to current term — a resolved cliff is a strong board-pack story.`,
          actions: [
            { label: "Open Board pack", to: "/schools/boardpack" },
          ],
        });
      }
    }

    if (scope === "portfolio") {
      if (f.grade !== "all") {
        const g = Number(f.grade);
        extra.push({
          tone: g >= 11 ? "positive" : "opportunity",
          title: `Grade ${g}: portfolio depth is ${g >= 11 ? "university-ready" : "still compounding"}`,
          body: g >= 11
            ? `Grade ${g} portfolios average the highest artifact counts. Anchor early-decision conversations and reference-letter drafts to these evidence trails.`
            : `Grade ${g} learners benefit most from a "one reflection per artifact" nudge. Small habit, outsized quality gains within one term.`,
          actions: [
            { label: "Open Student profile", to: "/schools/student" },
            { label: `Counsellor · Grade ${g}`, to: `/schools/counsellor?grade=${g}` },
          ],
        });
      }
      if (f.house !== "all") {
        extra.push({
          tone: "opportunity",
          title: `${f.house}: showcase a house portfolio night`,
          body: `Filtering by ${f.house} makes it easy to curate a house-level exhibition. Two to three spotlight portfolios per house is enough to shift parent perception.`,
          actions: [
            { label: `Principal · ${f.house}`, to: `/schools?house=${f.house}` },
          ],
        });
      }
      if (f.term !== "current") {
        extra.push({
          tone: "watch",
          title: `${termLabel(f.term)}: artifact velocity comparison`,
          body: `Compare avg. artifacts / student against the current term. A rising line is the single clearest board-pack visual for portfolio programmes.`,
          actions: [
            { label: "Open Board pack", to: "/schools/boardpack" },
          ],
        });
      }
    }

    return { insights: [...extra, ...base], contextLabel, isFiltered: true };
  }, [f, base, scope]);
}
